import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CARD_REVEAL = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const denied = location.state?.denied;

  useEffect(() => {
    if (!loading && user) navigate("/admin/dashboard", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-900 px-5">
      <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-slate-900/70 to-slate-900/90" />

      <motion.div
        {...CARD_REVEAL}
        className="relative z-10 w-full max-w-[420px] rounded-2xl border border-white/20 bg-white/95 p-8 shadow-2xl backdrop-blur-xl"
        data-testid="admin-login-card"
      >
        <img src={LOGO_URL} alt="Place des Énergies" className="mx-auto h-14 w-auto" />
        <div className="mt-7 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground">Espace conseillère</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Connectez-vous pour consulter et gérer les leads reçus.
          </p>
        </div>

        {denied && (
          <div className="mt-5 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700" data-testid="access-denied-alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Accès non autorisé : ce compte Google n'est pas habilité. Contactez un administrateur Place des Énergies.</span>
          </div>
        )}

        <button
          type="button"
          data-testid="google-login-button"
          onClick={login}
          disabled={loading}
          className="mt-8 flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 active:scale-[0.99] disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 002.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z" />
            </svg>
          )}
          Continuer avec Google
        </button>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Accès réservé aux conseillères Place des Énergies.
        </p>
      </motion.div>
    </div>
  );
}
